import { api } from './client';
import type { PayoutSlot } from '@/lib/payouts';

export type AdminTournament = {
  id: number;
  name: string;
  status: 'setup' | 'active' | 'completed' | 'archived';
  machineName: string;
  entryFeeCents: number;
  maxEntriesPerPlayer: number;
  pullCount: number;
  creditsPerPull: number;
  payoutStructure: PayoutSlot[];
  tiebreakerPulls: number;
  createdBy: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
};

export type AdminEntry = {
  id: number;
  userId: number;
  userDisplayName: string | null;
  userEmail: string | null;
  entryNumber: number;
  startingBalance: number | null;
  endingBalance: number | null;
  score: number | null;
  isTiebreaker: boolean;
  parentEntryId: number | null;
  paid: boolean;
  submittedAt: string | null;
  verifiedByAdmin: boolean;
  hasStartingPhoto: boolean;
  hasEndingPhoto: boolean;
  editCount: number;
};

export type AdminPayout = {
  id: number;
  userId: number;
  userDisplayName: string | null;
  place: number;
  amountCents: number;
  paid: boolean;
  paidAt: string | null;
};

export type AdminStanding = {
  userId: number;
  baseScore: number | null;
  baseEntryId: number | null;
  tiebreakerScores: number[];
  rank: number;
  tied: boolean;
};

export type AdminTie = {
  rank: number;
  userIds: number[];
  paying: boolean;
};

export type PendingTiebreaker = {
  rank: number;
  userIds: number[];
  /**
   * Sub-state of a pending tiebreaker round:
   *   - 'awaiting_scores'       — at least one tied user hasn't submitted yet
   *   - 'awaiting_verification' — all submitted, none verified by admin yet
   * Both gate the "Trigger tiebreaker" button on the manage page.
   */
  state: 'awaiting_scores' | 'awaiting_verification';
};

export type AdminTournamentDetail = {
  tournament: AdminTournament;
  entries: AdminEntry[];
  payouts: AdminPayout[];
  standings: AdminStanding[];
  ties: AdminTie[];
  potCents: number;
  pendingTiebreakers: PendingTiebreaker[];
  entryCount: number;
};

export type AdminUser = {
  id: number;
  email: string;
  displayName: string;
  phone: string | null;
  isAdmin: boolean;
  isApproved: boolean;
  createdAt: string;
};

export type AdminPendingSummary = {
  pendingUsers: number;
  unverifiedEntries: number;
  unpaidEntries: number;
  /** Active tournament id, or null if none is currently active. */
  activeTournamentId: number | null;
};

export type TournamentCreateInput = {
  name: string;
  machineName?: string;
  entryFeeCents: number;
  maxEntriesPerPlayer: number;
  pullCount: number;
  creditsPerPull: number;
  tiebreakerPulls: number;
  payoutStructure: PayoutSlot[];
};

export const adminApi = {
  listTournaments: () =>
    api.get<{ tournaments: AdminTournament[] }>('/api/admin/tournaments'),
  activeTournament: () =>
    api.get<{ tournament: AdminTournament | null }>('/api/admin/tournaments/active'),
  getTournament: (id: number) =>
    api.get<AdminTournamentDetail>(`/api/admin/tournaments/${id}`),
  createTournament: (input: TournamentCreateInput) =>
    api.post<{ tournament: AdminTournament }>('/api/admin/tournaments', input),
  updateTournament: (id: number, input: Partial<TournamentCreateInput>) =>
    api.patch<{ tournament: AdminTournament }>(`/api/admin/tournaments/${id}`, input),
  startTournament: (id: number) =>
    api.post<{ tournament: AdminTournament }>(
      `/api/admin/tournaments/${id}/start`,
    ),
  closeTournament: (id: number) =>
    api.post<{ tournament: AdminTournament }>(
      `/api/admin/tournaments/${id}/close`,
    ),
  deleteTournament: (id: number) =>
    api.del<{ ok: true }>(`/api/admin/tournaments/${id}`),
  tiebreaker: (id: number, userIds: number[]) =>
    api.post<{ ok: true; tiebreakerEntryIds: number[] }>(
      `/api/admin/tournaments/${id}/tiebreaker`,
      { userIds },
    ),

  editEntry: (
    id: number,
    input: { startingBalance?: number; endingBalance?: number; reason: string },
  ) => api.patch<{ ok: true }>(`/api/admin/entries/${id}`, input),
  entryHistory: (id: number) =>
    api.get<{
      history: Array<{
        id: number;
        adminUserId: number;
        adminDisplayName: string | null;
        previousScore: number | null;
        newScore: number | null;
        reason: string;
        editedAt: string;
      }>;
    }>(`/api/admin/entries/${id}/history`),
  toggleEntryPaid: (id: number) =>
    api.post<{ paid: boolean }>(`/api/admin/entries/${id}/toggle-paid`),
  toggleEntryVerified: (id: number) =>
    api.post<{ verifiedByAdmin: boolean }>(`/api/admin/entries/${id}/toggle-verified`),
  togglePayoutPaid: (id: number) =>
    api.post<{ paid: boolean }>(`/api/admin/payouts/${id}/toggle-paid`),

  listUsers: () => api.get<{ users: AdminUser[] }>('/api/admin/users'),
  setApproval: (id: number, isApproved: boolean) =>
    api.post<{ user: AdminUser }>(`/api/admin/users/${id}/approval`, {
      isApproved,
    }),

  pendingSummary: () =>
    api.get<AdminPendingSummary>('/api/admin/pending-summary'),
};
